import { Fragment, useRef, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setFormValue } from "../../redux/slices/expressionspageslice";
import { setCurrentPageIsDirty } from "../../redux/slices/currentpageslice";
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import {
  FormInput,
  FormCheckbox,
  FormDropDownList,
  FormTextArea,
  FormDropDownListById,
} from "../../forms/form-components";
import { DropDownButton } from "@progress/kendo-react-buttons";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { FormRegion } from "../../components/formregion";
import { FormToggleButton } from "../../components/togglebutton";
import { formValidator } from "./expressionspageformvalidator";
import { codeSnippets } from "./codesnippets";
import { disAllowedCharactersInTitle } from "../../settings";
import { useTranslation } from "react-i18next";

const codeSnippetsItemRenderer = (currentTheme, onItemClicked) => (props) => {
  if (props.item.text)
    return (
      <div
        title={props.item.tooltip}
        style={{ display: "flex", width: "100%", padding: 6 }}
        onClick={(e) => onItemClicked(props.item)}
      >
        <div title={props.item.tooltip} style={{ marginLeft: 0 }}>
          {props.item.text}
        </div>
      </div>
    );
  else
    return (
      <div className="menu-separator" onClick={(e) => e.preventDefault()} />
    );
};

export const ExpressionsPageForm = (props) => {
  const dispatch = useDispatch();
  const currentTheme = useSelector((state) => state.currentTheme);
  const pageState = useSelector((state) => state.expressionsPage);
  const dataTypes = pageState.lookups.dataTypes;
  const currentDataset = pageState.currentDataset;
  const updateDirtyState = props.updateDirtyState;
  const { t } = useTranslation();
  const localizedCodeSnippets = codeSnippets.map((snippet) => ({
    ...snippet,
    text: snippet.textKey ? t(snippet.textKey) : snippet.text,
  }));

  const checksList = [
    { id: 1, title: `${t("expressions.aggregate")}`},
    { id: 2, title: `${t("expressions.decimal")}`},
    { id: 3, title: `${t("expressions.percent")}`},
  ];

  //handle is dirty
  useEffect(() => {
    updateDirtyState();
  }, [pageState.formInitialValues, pageState.formValues]);

  return (
    <Fragment>
      <Form
        validator={formValidator}
        key={pageState.formKey}
        initialValues={{ ...pageState.formInitialValues }}
        render={(formRenderProps) => {
          return (
            <FormElement
              horizontal={false}
              style={
                {
                  // width: 250,
                }
              }
            >
              <FormRegion className="form-region-1">
                <div className="form-group form-group-1">
                  <Field
                    id={"Title"}
                    name={"Title"}
                    label={t("expressions.title1")}
                    component={FormInput}
                    onKeyPress={(e) => {
                      let v = e.key;
                      if (disAllowedCharactersInTitle.indexOf(v) >= 0) {
                        e.preventDefault();
                        return;
                      }
                    }}
                    onChange={(e) => {
                      dispatch(setFormValue({ id: "Title", value: e.value }));
                    }}
                    autoFocus={true}
                    disabled={
                      pageState.isProcessing || pageState.formValues.IsSystem
                    }
                    maxLength={50}
                  />
                  <Field
                    id={"IsVisibleToClient"}
                    name="IsVisibleToClient"
                    label={t("expressions.visible")}
                    component={FormCheckbox}
                    onChange={(e) => {
                      dispatch(
                        setFormValue({
                          id: "IsVisibleToClient",
                          value: e.target.value,
                        })
                      );
                    }}
                    disabled={
                      pageState.isProcessing || pageState.formValues.IsSystem
                    }
                  />
                  <Field
                    id={"DataTypeId"}
                    name={"DataTypeId"}
                    label={t("expressions.datatype")}
                    component={FormDropDownListById}
                    data={dataTypes}
                    dataItemKey="Id"
                    textField="Title"
                    onChange={(e) => {
                      dispatch(
                        setFormValue({ id: "DataTypeId", value: e.value })
                      );

                      if (props.onDataTypeChange) props.onDataTypeChange(e);
                    }}
                    disabled={
                      pageState.isProcessing || pageState.formValues.IsSystem
                    }
                  />
                  <Field
                    id={"FieldGroupId"}
                    name={"FieldGroupId"}
                    label={t("expressions.fieldgroup")}
                    component={FormDropDownListById}
                    data={currentDataset.FieldGroups.map((e) => {
                      return e.Id < 1
                        ? { ...e, Title: t("expressions.not_applicable") }
                        : { ...e };
                    })}
                    dataItemKey="Id"
                    textField="Title"
                    onChange={(e) => {
                      dispatch(
                        setFormValue({ id: "FieldGroupId", value: e.value })
                      );
                    }}
                    disabled={
                      pageState.isProcessing || pageState.formValues.IsSystem
                    }
                  />
                  <Field
                    id={"Description"}
                    name={"Description"}
                    label={t("expressions.description")}
                    component={FormTextArea}
                    style={{ height: 50 }}
                    onChange={(e) =>
                      dispatch(
                        setFormValue({ id: "Description", value: e.value })
                      )
                    }
                    disabled={
                      pageState.isProcessing || pageState.formValues.IsSystem
                    }
                    maxLength={255}
                  />
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginTop: 28,
                    }}
                  >
                    <div style={{ color: currentTheme.inputLabelColor }}>
                      {t("expressions.expressionbody")}
                    </div>
                    <Tooltip anchorElement="target" position="top">
                      <div title={t("expressions.insert")}>
                        <DropDownButton
                          disabled={
                            pageState.processing ||
                            pageState.formValues.IsSystem
                          }
                          tabIndex={-1}
                          className="buttons-container-button"
                          popupSettings={{ popupClass: "snippet-popup" }}
                          items={localizedCodeSnippets}
                          itemRender={codeSnippetsItemRenderer(
                            currentTheme,
                            (item) => {
                              props.onInsertCodeSnippet(
                                formRenderProps,
                                item.id
                              );
                            }
                          )}
                          icon="insert-up"
                          fillMode={"flat"}
                        />
                      </div>
                    </Tooltip>
                  </div>
                  <Field
                    id={"Body"}
                    name={"Body"}
                    component={FormTextArea}
                    style={{
                      height: 185,
                      fontFamily: "'courier new', monospace",
                      fontSize: 15,
                      fontWeight: 400,
                    }}
                    className="expression-body"
                    disabled={
                      pageState.isProcessing || pageState.formValues.IsSystem
                    }
                    onChange={(e) =>
                      dispatch(setFormValue({ id: "Body", value: e.value }))
                    }
                    maxLength={2000}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();

                      let func = e.dataTransfer.getData("func");

                      if (!func) return;

                      let currentBody = pageState.formValues.Body || "";

                      if (currentBody && currentBody != "") currentBody += " ";

                      currentBody += func;
                      dispatch(
                        setFormValue({ id: "Body", value: currentBody })
                      );
                      formRenderProps.onChange("Body", { value: currentBody });
                    }}
                  />
                  <div
                    className="checks"
                    style={{
                      display: "flex",
                      marginTop: 14,
                      justifyContent: "space-between",
                      borderTop: "none",
                      borderTopLeftRadius: 0,
                      borderTopRightRadius: 0,
                    }}
                  >
                    <Field
                      component={FormToggleButton}
                      values={checksList}
                      id="Checks"
                      name="Checks"
                      disabled={pageState.formValues.IsSystem}
                      onChange={(e) => {
                        let value = [...e.value];

                        dispatch(setFormValue({ id: "Checks", value: value }));
                        dispatch(
                          setFormValue({ id: "IsAggregate", value: value[0] })
                        );
                        dispatch(
                          setFormValue({ id: "IsDecimal", value: value[1] })
                        );
                        dispatch(
                          setFormValue({ id: "IsPercent", value: value[2] })
                        );
                      }}
                    />
                  </div>
                </div>
              </FormRegion>
            </FormElement>
          );
        }}
      />
      <style>
        {`        
                    .expressions-page-form .k-form
                    {          
                        /*the kendo form which is child of my form.content*/     
                        width: 100%;
                        display: flex;
                        flex: 1;
                        justify-content: space-between;
                        padding: 0 24px 12px 24px;/*keep 12px in right and bottom so that when minimizing we keep a white region to the right and bottom; also keep 8px on the left*/
                    }

                    .expressions-page-form .form-region-1
                    {
                        width: 100%;
                        max-width: 1000px;
                        margin-top: 20px !important;/*to override kendo first child of k-form having marginTop=0*/                     
                    }

                    .expressions-page-form .form-region-1 .form-group-1
                    {
                        display: flex;
                        flex-direction: column;
                        width: 100%;
                    }

                   
                    .expressions-page-form > .content
                    {
                        padding: 0px;
                        background-color: transparent;
                        align-items: flex-start;
                    }

                    .expressions-page-form .form-region-root
                    {
                        background-color: transparent;
                    }
                    
                    .expressions-page-form .form-region-root .k-form-field:nth-child(2)
                    {
                        margin-top: 18px !important;
                    }

                    .expressions-page-form .form-region-root .k-form-field:nth-child(7)
                    {
                        margin-top: -2px !important;
                    }

                    .expressions-page-form .form-group-1 .toggle-container
                    {
                        /*the div containing the 3 checks*/
                        justify-content: space-between;
                        border-color: ${currentTheme.componentBorderColor};
                        width: 100%;/*620px;*/
                        max-width: 1000px;
                    }

                    .expressions-page-form input.k-input, .expressions-page-form textarea.k-textarea, 
                    .expressions-page-form .k-dropdownlist .k-input-inner
                    {
                        /*make input controls have a bit more height*/
                        padding: 6px 8px;
                    } 

                    .snippet-popup .k-menu-item:nth-child(2)
                    {
                        padding: 0 !important;
                    }

                    .snippet-popup .k-menu-item:nth-child(1),
                    .snippet-popup .k-menu-item:nth-child(3)
                    {
                        padding-top: 3px;
                        padding-bottom: 3px;
                    }


                    .expressions-page-form .k-form-field textarea
                    {
                        height: 200px !important;
                    }

                    @media (max-width: 1024px)
                    {
                        .expressions-page-form .k-form
                        {
                            padding: 0 16px 16px;
                            overflow-x: hidden;
                        }

                        .expressions-page-form .form-region-1
                        {
                            margin-top: 12px !important;
                        }

                        .expressions-page-form .form-region-root .k-form-field
                        {
                            margin-top: 20px;
                        }

                        .expressions-page-form .form-region-root .k-form-field:nth-child(1)
                        {
                            margin-top: 4px;
                        }

                        .expressions-page-form .form-region-root .k-form-field:nth-child(6)
                        {
                            margin-top: 0 !important;
                        }

                        .expressions-page-form .k-form-field textarea
                        {
                            height: 160px !important;
                        }
                    }
                `}
      </style>
    </Fragment>
  );
};
